import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, Sparkles, Target, TrendingUp, Users, ChevronRight } from 'lucide-react';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      icon: Target,
      title: "OKRs Inteligentes",
      description: "Gestiona objetivos y resultados clave con precisión quirúrgica"
    },
    {
      icon: Sparkles,
      title: "IA Integrada",
      description: "Genera tareas y analiza riesgos automáticamente con GPT-4"
    },
    {
      icon: TrendingUp,
      title: "Predicción de Riesgo",
      description: "Detecta KRs en peligro antes de que sea tarde"
    },
    {
      icon: Users,
      title: "Multi-Empresa",
      description: "Gobierna múltiples compañías desde un solo dashboard"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [features.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('¡Bienvenido de vuelta!');
      } else {
        await register(name, email, password, 'owner');
        toast.success('Cuenta creada exitosamente');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0a0a0a] relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-emerald-500/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-amber-500/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="text-[#0a0a0a] text-lg font-black">U</span>
            </div>
            <span className="text-white text-xl font-semibold tracking-tight">UrpeOS</span>
          </div>

          {/* Main content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-white leading-tight">
                El sistema operativo<br />
                para tus <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">OKRs</span>
              </h1>
              <p className="text-gray-400 mt-4 text-lg max-w-md">
                Gobierna la ejecución de tu empresa con inteligencia artificial y datos en tiempo real.
              </p>
            </div>

            {/* Feature carousel */}
            <div className="space-y-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                const isActive = index === activeFeature;
                return (
                  <div
                    key={index}
                    onClick={() => setActiveFeature(index)}
                    className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-500 ${
                      isActive 
                        ? 'bg-white/10 border border-white/20' 
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isActive ? 'bg-white text-[#0a0a0a]' : 'bg-white/10 text-gray-400'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-medium transition-colors ${isActive ? 'text-white' : 'text-gray-400'}`}>
                        {feature.title}
                      </h3>
                      <p className={`text-sm transition-all duration-500 ${
                        isActive ? 'text-gray-400 max-h-20 opacity-100 mt-1' : 'max-h-0 opacity-0 overflow-hidden'
                      }`}>
                        {feature.description}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-all ${
                      isActive ? 'text-white translate-x-1' : 'text-gray-600'
                    }`} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <span>© 2026 Urpe</span>
            <span className="w-1 h-1 bg-gray-600 rounded-full" />
            <span>Sistema interno</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#fafafa]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#0a0a0a] rounded-lg flex items-center justify-center">
              <span className="text-white text-lg font-black">U</span>
            </div>
            <span className="text-[#0a0a0a] text-xl font-semibold tracking-tight">UrpeOS</span>
          </div>

          {/* Auth header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#0a0a0a]">
              {isLogin ? 'Bienvenido de vuelta' : 'Crear cuenta'}
            </h2>
            <p className="text-gray-500 mt-2">
              {isLogin 
                ? 'Ingresa tus credenciales para continuar' 
                : 'Completa el formulario para comenzar'}
            </p>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-gray-200 hover:bg-white hover:border-gray-300 text-[#0a0a0a] font-medium bg-white shadow-sm"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continuar con Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#fafafa] px-4 text-sm text-gray-400">o</span>
            </div>
          </div>

          {/* Auth Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 bg-white border-gray-200 focus:border-[#0a0a0a] focus:ring-[#0a0a0a] rounded-lg"
                  placeholder="Tu nombre completo"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-white border-gray-200 focus:border-[#0a0a0a] focus:ring-[#0a0a0a] rounded-lg"
                placeholder="tu@email.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 bg-white border-gray-200 focus:border-[#0a0a0a] focus:ring-[#0a0a0a] rounded-lg"
                placeholder="Contraseña"
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white font-medium rounded-lg group"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isLogin ? 'Entrando...' : 'Creando cuenta...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isLogin ? 'Entrar' : 'Crear cuenta'}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          {/* Toggle auth mode */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-gray-500 hover:text-[#0a0a0a] transition-colors"
              data-testid={isLogin ? "register-tab" : "login-tab"}
            >
              {isLogin ? (
                <>¿No tienes cuenta? <span className="font-medium text-[#0a0a0a]">Regístrate</span></>
              ) : (
                <>¿Ya tienes cuenta? <span className="font-medium text-[#0a0a0a]">Inicia sesión</span></>
              )}
            </button>
          </div>

          {/* Stats */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#0a0a0a]">5</p>
                <p className="text-xs text-gray-500 mt-1">Empresas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#0a0a0a]">109</p>
                <p className="text-xs text-gray-500 mt-1">Key Results</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#0a0a0a]">Q1</p>
                <p className="text-xs text-gray-500 mt-1">2026</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
